import { memo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid2,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import {
  VerifiedUser,
  ExpandMore,
  Warning,
  CheckCircle,
  Build,
  ElectricalServices,
  Plumbing,
  Roofing,
  AcUnit,
  Construction,
} from "@mui/icons-material";

// Common trade licensing info
const TRADE_LICENSES = [
  {
    icon: ElectricalServices,
    trade: "Electrical",
    description:
      "Electricians must be licensed in all 50 states. Most require passing exams, completing apprenticeships, and meeting experience requirements.",
    levels: ["Apprentice", "Journeyman", "Master Electrician"],
    typicalRequirements: [
      "4-5 years apprenticeship",
      "State exam",
      "Continuing education",
    ],
  },
  {
    icon: Plumbing,
    trade: "Plumbing",
    description:
      "Plumbers are licensed at state or local levels. Requirements include apprenticeship training and passing practical and written exams.",
    levels: ["Apprentice", "Journeyman", "Master Plumber"],
    typicalRequirements: [
      "4-5 years apprenticeship",
      "Journeyman exam",
      "Master exam (additional experience)",
    ],
  },
  {
    icon: AcUnit,
    trade: "HVAC",
    description:
      "HVAC technicians need EPA Section 608 certification to handle refrigerants. Many states require additional state licensing.",
    levels: ["EPA Certified", "State Licensed"],
    typicalRequirements: [
      "EPA 608 certification",
      "State license (varies)",
      "Manufacturer training",
    ],
  },
  {
    icon: Roofing,
    trade: "Roofing",
    description:
      "Roofing contractor requirements vary widely. Some states require specific roofing licenses while others include it under general contractor licenses.",
    levels: ["Registered Contractor", "Licensed Contractor"],
    typicalRequirements: [
      "General contractor license (most states)",
      "Insurance requirements",
      "Bond requirements",
    ],
  },
  {
    icon: Construction,
    trade: "General Contractor",
    description:
      "General contractors coordinate construction projects. Most states require licensing for projects above a certain dollar threshold.",
    levels: ["Residential", "Commercial", "Unlimited"],
    typicalRequirements: [
      "Business license",
      "State contractor exam",
      "Proof of insurance",
      "Surety bond",
    ],
  },
];

// State-specific info categories
const STATE_CATEGORIES = [
  {
    category: "Strict Licensing States",
    description:
      "These states have comprehensive licensing requirements for most trades and enforce them rigorously.",
    states: ["California", "Florida", "Arizona", "Nevada", "Louisiana"],
    color: "#1565C0",
  },
  {
    category: "Moderate Licensing States",
    description:
      "States with licensing requirements for major trades but may allow local jurisdictions to set additional rules.",
    states: [
      "Texas",
      "Colorado",
      "Oregon",
      "Washington",
      "Georgia",
      "North Carolina",
    ],
    color: "#7b1fa2",
  },
  {
    category: "Local Control States",
    description:
      "These states leave most contractor licensing to local governments. Requirements can vary significantly by city or county.",
    states: ["Kansas", "Missouri", "Indiana", "Wyoming", "Vermont"],
    color: "#ed6c02",
  },
];

// What to verify
const VERIFICATION_CHECKLIST = [
  {
    item: "License Number",
    description: "Ask for their license number and verify it with the state licensing board.",
  },
  {
    item: "Insurance Coverage",
    description:
      "Confirm they have general liability insurance and workers' compensation coverage.",
  },
  {
    item: "Bond Status",
    description:
      "Many states require contractors to be bonded. Verify their bond is current.",
  },
  {
    item: "Complaint History",
    description:
      "Check with your state's contractor board for any complaints or disciplinary actions.",
  },
  {
    item: "References",
    description:
      "Request and contact references from recent projects similar to yours.",
  },
  {
    item: "Written Contract",
    description:
      "Always get a detailed written contract including scope, timeline, and payment schedule.",
  },
];

const LicensingRequirements = memo(() => {
  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #5c6bc0 0%, #7986cb 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
            mt: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <VerifiedUser
              sx={{ fontSize: { xs: 32, md: 48 }, color: "#ffffff", mr: 2 }}
            />
            <Typography
              variant="h2"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.2,
              }}
            >
              Licensing Requirements
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
              maxWidth: 800,
            }}
          >
            Hiring licensed contractors protects you and your investment.
            Understanding licensing requirements helps you verify that
            professionals working on your home meet state and local standards.
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 2, md: 4 },
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          {/* Why It Matters */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#fff3e0",
              border: "1px solid #ffb74d",
              mb: 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "#e65100", fontWeight: 600, mb: 2 }}
            >
              <Warning sx={{ mr: 1, verticalAlign: "middle" }} />
              Why Licensing Matters
            </Typography>
            <Typography variant="body1" sx={{ color: "#555", lineHeight: 1.8 }}>
              Unlicensed contractors may offer lower prices, but hiring them can
              void warranties, create liability issues, cause permit problems
              when selling, and leave you without recourse if work is
              substandard. <strong>Licensed contractors</strong> have
              demonstrated competency, carry required insurance, and are
              accountable to state licensing boards.
            </Typography>
          </Box>

          {/* Trade-Specific Licensing */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Trade-Specific Licensing
            </Typography>
            <Box>
              {TRADE_LICENSES.map((trade, index) => (
                <Accordion
                  key={index}
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px !important",
                    mb: 2,
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      margin: "0 0 16px 0",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <trade.icon sx={{ color: "#fff", fontSize: 22 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      {trade.trade}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: "#555", lineHeight: 1.8, mb: 3 }}
                    >
                      {trade.description}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#333", mb: 1 }}
                      >
                        License Levels:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {trade.levels.map((level, i) => (
                          <Chip
                            key={i}
                            label={level}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#333", mb: 1 }}
                      >
                        Typical Requirements:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {trade.typicalRequirements.map((req, i) => (
                          <Typography
                            key={i}
                            component="li"
                            variant="body2"
                            sx={{ color: "#666", lineHeight: 1.8 }}
                          >
                            {req}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          {/* State Categories */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 2,
              }}
            >
              State Licensing Overview
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 4 }}
            >
              Contractor licensing requirements vary significantly by state.
              Here's a general overview:
            </Typography>
            <Grid2 container spacing={3}>
              {STATE_CATEGORIES.map((cat, index) => (
                <Grid2 key={index} size={{ xs: 12, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: "12px",
                      borderTop: `4px solid ${cat.color}`,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ color: cat.color, fontWeight: 600, mb: 2 }}
                      >
                        {cat.category}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.7, mb: 3 }}
                      >
                        {cat.description}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#333", mb: 1 }}
                      >
                        Examples:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {cat.states.map((state, i) => (
                          <Chip
                            key={i}
                            label={state}
                            size="small"
                            sx={{
                              backgroundColor: `${cat.color}15`,
                              color: cat.color,
                              fontSize: "0.75rem",
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Verification Checklist */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              <Build sx={{ mr: 1, verticalAlign: "middle" }} />
              Before Hiring: Verification Checklist
            </Typography>
            <Grid2 container spacing={2}>
              {VERIFICATION_CHECKLIST.map((item, index) => (
                <Grid2 key={index} size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <CheckCircle
                      sx={{ color: "#4caf50", mr: 2, mt: 0.5, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.item}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Resources */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "#1565C0", fontWeight: 600, mb: 3 }}
            >
              Verify a Contractor's License
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
            >
              Most states have online databases where you can verify a
              contractor's license status, check for complaints, and confirm
              insurance coverage. Search for your state's contractor licensing
              board or use these resources:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>California:</strong> Contractors State License Board
                (CSLB)
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Florida:</strong> Department of Business & Professional
                Regulation
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Texas:</strong> Texas Department of Licensing and
                Regulation
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Other States:</strong> Search "[Your State] contractor
                license verification"
              </Typography>
            </Box>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default LicensingRequirements;
